import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  BarChart3,
  Video,
  MousePointer,
  Minus,
  Star,
  QrCode,
  PenTool,
  Calendar,
  TrendingUp,
  List,
  Upload,
  Link,
  Crop,
  Palette,
  RotateCw,
  Trash2,
  Heading1,
  Heading2,
  Heading3,
  Type,
  AlignLeft,
  Variable,
  Settings,
  Table as TableIcon,
  LineChart,
  PieChart,
  Activity,
  Gauge,
  Database,
  Award,
  Quote,
  AlertCircle,
  Frame,
  Clock,
  ChevronRight,
  Zap,
  Code
} from 'lucide-react';

// Advanced Block Types
export const ADVANCED_BLOCK_TEMPLATES = [
  {
    id: 'heading1',
    type: 'heading1' as const,
    name: 'Heading 1',
    icon: Heading1,
    defaultSize: { width: 400, height: 60 },
    defaultContent: {
      text: 'Main Heading',
      fontSize: 32,
      fontWeight: 'bold',
      color: '#1f2937',
      textAlign: 'left',
      variables: []
    },
  },
  {
    id: 'heading2',
    type: 'heading2' as const,
    name: 'Heading 2',
    icon: Heading2,
    defaultSize: { width: 350, height: 50 },
    defaultContent: {
      text: 'Section Heading',
      fontSize: 24,
      fontWeight: 'bold',
      color: '#374151',
      textAlign: 'left',
      variables: []
    },
  },
  {
    id: 'heading3',
    type: 'heading3' as const,
    name: 'Heading 3',
    icon: Heading3,
    defaultSize: { width: 300, height: 40 },
    defaultContent: {
      text: 'Sub Heading',
      fontSize: 18,
      fontWeight: 'semibold',
      color: '#4b5563',
      textAlign: 'left',
      variables: []
    },
  },
  {
    id: 'paragraph',
    type: 'paragraph' as const,
    name: 'Paragraph',
    icon: Type,
    defaultSize: { width: 400, height: 120 },
    defaultContent: {
      text: 'This is a paragraph with rich text formatting. You can add variables like {{name}} and {{date}} anywhere in the text.',
      fontSize: 14,
      fontWeight: 'normal',
      color: '#6b7280',
      textAlign: 'left',
      lineHeight: 1.6,
      variables: []
    },
  },
  {
    id: 'variable',
    type: 'variable' as const,
    name: 'Variable',
    icon: Variable,
    defaultSize: { width: 150, height: 30 },
    defaultContent: {
      variableName: 'name',
      placeholder: 'Enter value...',
      defaultValue: '',
      options: ['Option 1', 'Option 2', 'Option 3'],
      type: 'dropdown' // 'text', 'dropdown', 'date', 'number'
    },
  },
  {
    id: 'chart',
    type: 'chart' as const,
    name: 'Chart',
    icon: BarChart3,
    defaultSize: { width: 300, height: 200 },
    defaultContent: {
      chartType: 'bar',
      data: [
        { label: 'Jan', value: 30 },
        { label: 'Feb', value: 45 },
        { label: 'Mar', value: 60 },
        { label: 'Apr', value: 40 }
      ],
      colors: ['#3b82f6', '#ef4444', '#10b981', '#f59e0b']
    },
  },
  {
    id: 'video',
    type: 'video' as const,
    name: 'Video',
    icon: Video,
    defaultSize: { width: 400, height: 225 },
    defaultContent: { src: '', poster: '', autoplay: false },
  },
  {
    id: 'button',
    type: 'button' as const,
    name: 'Button',
    icon: MousePointer,
    defaultSize: { width: 120, height: 40 },
    defaultContent: { 
      text: 'Click Me', 
      link: '', 
      style: 'primary',
      size: 'medium'
    },
  },
  {
    id: 'divider',
    type: 'divider' as const,
    name: 'Divider',
    icon: Minus,
    defaultSize: { width: 200, height: 2 },
    defaultContent: { 
      orientation: 'horizontal', 
      color: '#e5e7eb', 
      thickness: 2,
      style: 'solid'
    },
  },
  {
    id: 'icon',
    type: 'icon' as const,
    name: 'Icon',
    icon: Star,
    defaultSize: { width: 48, height: 48 },
    defaultContent: { 
      iconName: 'star', 
      color: '#3b82f6', 
      size: 48,
      style: 'filled'
    },
  },
  {
    id: 'qrcode',
    type: 'qrcode' as const,
    name: 'QR Code',
    icon: QrCode,
    defaultSize: { width: 120, height: 120 },
    defaultContent: { 
      value: 'https://example.com', 
      size: 120,
      errorLevel: 'M',
      includeMargin: true
    },
  },
  {
    id: 'signature',
    type: 'signature' as const,
    name: 'Signature',
    icon: PenTool,
    defaultSize: { width: 200, height: 80 },
    defaultContent: { 
      placeholder: 'Signature', 
      required: false,
      style: 'underline'
    },
  },
  {
    id: 'datetime',
    type: 'datetime' as const,
    name: 'Date/Time',
    icon: Calendar,
    defaultSize: { width: 150, height: 30 },
    defaultContent: { 
      format: 'MM/DD/YYYY', 
      showTime: false,
      timezone: 'local',
      defaultValue: 'current'
    },
  },
  {
    id: 'progress',
    type: 'progress' as const,
    name: 'Progress',
    icon: TrendingUp,
    defaultSize: { width: 200, height: 20 },
    defaultContent: { 
      value: 50, 
      max: 100,
      color: '#3b82f6',
      showLabel: true,
      style: 'bar'
    },
  },
  {
    id: 'list',
    type: 'list' as const,
    name: 'List',
    icon: List,
    defaultSize: { width: 200, height: 120 },
    defaultContent: { 
      items: ['Item 1', 'Item 2', 'Item 3'],
      listType: 'bullet',
      style: 'default'
    },
  },
  // Data Visualization Templates (PowerBI-like)
  {
    id: 'bar-chart',
    type: 'bar-chart' as const,
    name: 'Bar Chart',
    icon: BarChart3,
    defaultSize: { width: 400, height: 300 },
    defaultContent: {
      data: {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
        datasets: [{
          label: 'Sales',
          data: [12, 19, 3, 5, 2, 3],
          backgroundColor: '#3b82f6',
          borderColor: '#1e40af',
          borderWidth: 1
        }]
      },
      title: 'Monthly Sales',
      showLegend: true,
      showGrid: true,
      orientation: 'vertical'
    },
  },
  {
    id: 'line-chart',
    type: 'line-chart' as const,
    name: 'Line Chart',
    icon: LineChart,
    defaultSize: { width: 400, height: 300 },
    defaultContent: {
      data: {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
        datasets: [{
          label: 'Revenue',
          data: [65, 59, 80, 81, 56, 55],
          backgroundColor: 'rgba(34, 197, 94, 0.2)',
          borderColor: '#22c55e',
          borderWidth: 2,
          fill: true
        }]
      },
      title: 'Revenue Trend',
      showLegend: true,
      showGrid: true,
      smooth: true
    },
  },
  {
    id: 'pie-chart',
    type: 'pie-chart' as const,
    name: 'Pie Chart',
    icon: PieChart,
    defaultSize: { width: 350, height: 350 },
    defaultContent: {
      data: {
        labels: ['Desktop', 'Mobile', 'Tablet'],
        datasets: [{
          label: 'Traffic Sources',
          data: [55, 35, 10],
          backgroundColor: ['#3b82f6', '#10b981', '#f59e0b'],
          borderColor: ['#1e40af', '#059669', '#d97706'],
          borderWidth: 2
        }]
      },
      title: 'Traffic Sources',
      showLegend: true,
      showLabels: true,
      donut: false
    },
  },
  {
    id: 'kpi-card',
    type: 'kpi-card' as const,
    name: 'KPI Card',
    icon: TrendingUp,
    defaultSize: { width: 250, height: 150 },
    defaultContent: {
      title: 'Total Revenue',
      value: '$125,430',
      subtitle: 'This month',
      trend: 'up',
      trendValue: '+12.5%',
      icon: 'TrendingUp',
      color: '#22c55e'
    },
  },
  {
    id: 'progress-bar',
    type: 'progress-bar' as const,
    name: 'Progress Bar',
    icon: Activity,
    defaultSize: { width: 300, height: 80 },
    defaultContent: {
      value: 75,
      max: 100,
      label: 'Project Completion',
      showPercentage: true,
      color: '#3b82f6',
      height: 20
    },
  },
  {
    id: 'gauge-chart',
    type: 'gauge-chart' as const,
    name: 'Gauge Chart',
    icon: Gauge,
    defaultSize: { width: 300, height: 200 },
    defaultContent: {
      value: 75,
      min: 0,
      max: 100,
      title: 'Performance',
      unit: '%',
      color: '#22c55e',
      ranges: [
        { min: 0, max: 30, color: '#ef4444', label: 'Low' },
        { min: 30, max: 70, color: '#f59e0b', label: 'Medium' },
        { min: 70, max: 100, color: '#22c55e', label: 'High' }
      ]
    },
  },
  {
    id: 'data-table',
    type: 'data-table' as const,
    name: 'Data Table',
    icon: Database,
    defaultSize: { width: 500, height: 300 },
    defaultContent: {
      headers: ['Name', 'Role', 'Department', 'Salary'],
      rows: [
        ['John Doe', 'Manager', 'Sales', '$75,000'],
        ['Jane Smith', 'Developer', 'IT', '$85,000'],
        ['Bob Johnson', 'Analyst', 'Finance', '$65,000'],
        ['Alice Brown', 'Designer', 'Marketing', '$70,000']
      ],
      sortable: true,
      searchable: true,
      pagination: false,
      striped: true
    },
  },
  // Design Elements Templates (Canva-like)
  {
    id: 'badge',
    type: 'badge' as const,
    name: 'Badge',
    icon: Award,
    defaultSize: { width: 100, height: 30 },
    defaultContent: {
      text: 'New',
      variant: 'primary',
      size: 'medium',
      rounded: true
    },
  },
  {
    id: 'quote',
    type: 'quote' as const,
    name: 'Quote',
    icon: Quote,
    defaultSize: { width: 400, height: 120 },
    defaultContent: {
      text: 'The best way to predict the future is to create it.',
      author: 'Peter Drucker',
      citation: 'Management Consultant',
      style: 'blockquote'
    },
  },
  {
    id: 'callout',
    type: 'callout' as const,
    name: 'Callout',
    icon: AlertCircle,
    defaultSize: { width: 350, height: 100 },
    defaultContent: {
      title: 'Important Note',
      text: 'This is an important message that needs attention.',
      type: 'info',
      icon: 'AlertCircle'
    },
  },
  {
    id: 'border-frame',
    type: 'border-frame' as const,
    name: 'Frame',
    icon: Frame,
    defaultSize: { width: 300, height: 200 },
    defaultContent: {
      borderStyle: 'solid',
      borderWidth: 2,
      borderColor: '#3b82f6',
      borderRadius: 8,
      padding: 16
    },
  },
  {
    id: 'timeline',
    type: 'timeline' as const,
    name: 'Timeline',
    icon: Clock,
    defaultSize: { width: 400, height: 300 },
    defaultContent: {
      items: [
        { id: '1', title: 'Project Start', description: 'Initial planning phase', date: '2024-01-01', status: 'completed' },
        { id: '2', title: 'Development', description: 'Core development work', date: '2024-02-01', status: 'current' },
        { id: '3', title: 'Testing', description: 'Quality assurance', date: '2024-03-01', status: 'upcoming' },
        { id: '4', title: 'Launch', description: 'Product release', date: '2024-04-01', status: 'upcoming' }
      ],
      orientation: 'vertical'
    },
  },
  {
    id: 'accordion',
    type: 'accordion' as const,
    name: 'Accordion',
    icon: ChevronRight,
    defaultSize: { width: 400, height: 200 },
    defaultContent: {
      items: [
        { id: '1', title: 'Section 1', content: 'Content for section 1', isOpen: true },
        { id: '2', title: 'Section 2', content: 'Content for section 2', isOpen: false },
        { id: '3', title: 'Section 3', content: 'Content for section 3', isOpen: false }
      ],
      allowMultiple: false
    },
  },
  {
    id: 'icon-block',
    type: 'icon-block' as const,
    name: 'Icon',
    icon: Zap,
    defaultSize: { width: 60, height: 60 },
    defaultContent: {
      iconName: 'Zap',
      size: 48,
      color: '#3b82f6',
      style: 'outline'
    },
  },
  {
    id: 'script',
    type: 'script' as const,
    name: 'Script Block',
    icon: Code,
    defaultSize: { width: 400, height: 300 },
    defaultContent: {
      html: '',
      css: '',
      javascript: '',
      mode: 'preview'
    },
  },
];

// Advanced Image Upload Component
interface ImageUploadProps {
  onImageSelect: (file: File | string) => void;
  currentSrc?: string;
}

export const ImageUploadComponent: React.FC<ImageUploadProps> = ({ 
  onImageSelect, 
  currentSrc 
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (file: File) => {
    if (file.type.startsWith('image/')) {
      onImageSelect(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    
    if (imageFile) {
      handleFileSelect(imageFile);
    }
  };

  const handleUrlSubmit = () => {
    if (urlInput.trim()) {
      onImageSelect(urlInput.trim());
      setUrlInput('');
      setShowUrlInput(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Current Image Preview */}
      {currentSrc && (
        <div className="relative">
          <img 
            src={currentSrc} 
            alt="Current" 
            className="w-full h-32 object-cover rounded border"
          />
          <button
            onClick={() => onImageSelect('')}
            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
          dragOver 
            ? 'border-blue-400 bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
        <p className="text-sm text-gray-600 mb-2">
          Drag & drop an image here
        </p>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
        >
          Browse Files
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file);
          }}
        />
      </div>

      {/* URL Input */}
      <div className="space-y-2">
        {!showUrlInput ? (
          <button
            onClick={() => setShowUrlInput(true)}
            className="w-full flex items-center justify-center px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
          >
            <Link className="w-4 h-4 mr-2" />
            Add from URL
          </button>
        ) : (
          <div className="flex space-x-2">
            <input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
              onKeyPress={(e) => e.key === 'Enter' && handleUrlSubmit()}
            />
            <button
              onClick={handleUrlSubmit}
              className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
            >
              Add
            </button>
            <button
              onClick={() => {
                setShowUrlInput(false);
                setUrlInput('');
              }}
              className="px-2 py-1 bg-gray-300 text-gray-700 text-xs rounded hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Simple Text Editor Component (for better performance)
interface SimpleTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  style?: any;
  placeholder?: string;
}

export const SimpleTextEditor: React.FC<SimpleTextEditorProps> = React.memo(({
  content,
  onChange,
  style = {},
  placeholder = 'Enter text...'
}) => {
  const [localContent, setLocalContent] = useState(content);
  const [isEditing, setIsEditing] = useState(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();

  // Debounced onChange to prevent excessive updates
  const debouncedOnChange = useCallback((newContent: string) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      onChange(newContent);
    }, 150); // 150ms debounce
  }, [onChange]);

  useEffect(() => {
    if (!isEditing) {
      setLocalContent(content);
    }
  }, [content, isEditing]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setLocalContent(newContent);
    debouncedOnChange(newContent);
  }, [debouncedOnChange]);

  const handleFocus = useCallback(() => {
    setIsEditing(true);
    setLocalContent(content);
  }, [content]);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    // Ensure final value is synced
    onChange(localContent);
  }, [localContent, onChange]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Prevent event bubbling for certain keys to avoid canvas shortcuts
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.stopPropagation();
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.blur();
    }
  }, []);

  return (
    <textarea
      value={isEditing ? localContent : content}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className={`w-full h-full resize-none border-none outline-none bg-transparent no-drag ${isEditing ? 'ring-2 ring-blue-500' : ''}`}
      style={style}
    />
  );
});

// Dynamic Chart Component
interface ChartProps {
  data: Array<{ label: string; value: number }>;
  colors: string[];
  chartType: 'bar' | 'line' | 'pie';
  onDataChange: (data: Array<{ label: string; value: number }>) => void;
  onChartTypeChange: (type: 'bar' | 'line' | 'pie') => void;
}

export const SimpleChart: React.FC<ChartProps> = ({
  data,
  colors,
  chartType,
  onDataChange,
  onChartTypeChange
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const maxValue = Math.max(...data.map(d => d.value));

  const addDataPoint = () => {
    const newData = [...data, { label: `Item ${data.length + 1}`, value: 10 }];
    onDataChange(newData);
  };

  const removeDataPoint = (index: number) => {
    const newData = data.filter((_, i) => i !== index);
    onDataChange(newData);
  };

  const updateDataPoint = (index: number, field: 'label' | 'value', value: string | number) => {
    const newData = [...data];
    newData[index] = { ...newData[index], [field]: value };
    onDataChange(newData);
  };

  if (isEditing) {
    return (
      <div className="w-full h-full p-4 bg-white border border-blue-500 rounded">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-semibold">Edit Chart Data</h3>
          <div className="flex space-x-2">
            <select
              value={chartType}
              onChange={(e) => onChartTypeChange(e.target.value as 'bar' | 'line' | 'pie')}
              className="text-xs border rounded px-2 py-1"
            >
              <option value="bar">Bar Chart</option>
              <option value="pie">Pie Chart</option>
              <option value="line">Line Chart</option>
            </select>
            <button
              onClick={() => setIsEditing(false)}
              className="text-xs bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600"
            >
              Done
            </button>
          </div>
        </div>

        <div className="space-y-2 max-h-32 overflow-y-auto">
          {data.map((item, index) => (
            <div key={index} className="flex items-center space-x-2">
              <input
                type="text"
                value={item.label}
                onChange={(e) => updateDataPoint(index, 'label', e.target.value)}
                className="flex-1 text-xs border rounded px-2 py-1"
                placeholder="Label"
              />
              <input
                type="number"
                value={item.value}
                onChange={(e) => updateDataPoint(index, 'value', parseFloat(e.target.value) || 0)}
                className="w-16 text-xs border rounded px-2 py-1"
                placeholder="Value"
              />
              <button
                onClick={() => removeDataPoint(index)}
                className="text-xs bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
              >
                ×
              </button>
            </div>
          ))}
        </div>

        <button
          onClick={addDataPoint}
          className="mt-2 text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
        >
          + Add Data Point
        </button>
      </div>
    );
  }

  if (chartType === 'bar') {
    return (
      <div
        className="w-full h-full p-2 cursor-pointer hover:bg-gray-50 relative group"
        onClick={() => setIsEditing(true)}
      >
        <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="text-xs bg-gray-200 px-1 py-0.5 rounded">Edit</span>
        </div>
        <div className="flex items-end justify-around h-full space-x-1">
          {data.map((item, index) => (
            <div key={index} className="flex flex-col items-center flex-1">
              <div
                className="w-full rounded-t"
                style={{
                  height: `${(item.value / maxValue) * 70}%`,
                  backgroundColor: colors[index % colors.length],
                  minHeight: '4px'
                }}
              />
              <span className="text-xs mt-1 text-center truncate">{item.label}</span>
              <span className="text-xs text-gray-500">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (chartType === 'pie') {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    let currentAngle = 0;

    return (
      <div
        className="w-full h-full flex items-center justify-center cursor-pointer hover:bg-gray-50 relative group"
        onClick={() => setIsEditing(true)}
      >
        <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="text-xs bg-gray-200 px-1 py-0.5 rounded">Edit</span>
        </div>
        <div className="relative">
          <svg width="80" height="80" viewBox="0 0 100 100">
            {data.map((item, index) => {
              const percentage = item.value / total;
              const angle = percentage * 360;
              const startAngle = currentAngle;
              const endAngle = currentAngle + angle;

              const x1 = 50 + 40 * Math.cos((startAngle - 90) * Math.PI / 180);
              const y1 = 50 + 40 * Math.sin((startAngle - 90) * Math.PI / 180);
              const x2 = 50 + 40 * Math.cos((endAngle - 90) * Math.PI / 180);
              const y2 = 50 + 40 * Math.sin((endAngle - 90) * Math.PI / 180);

              const largeArcFlag = angle > 180 ? 1 : 0;

              const pathData = [
                `M 50 50`,
                `L ${x1} ${y1}`,
                `A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                'Z'
              ].join(' ');

              currentAngle += angle;

              return (
                <path
                  key={index}
                  d={pathData}
                  fill={colors[index % colors.length]}
                  stroke="white"
                  strokeWidth="1"
                />
              );
            })}
          </svg>
          <div className="absolute top-full left-0 mt-1 space-y-0.5 text-xs max-w-24">
            {data.map((item, index) => (
              <div key={index} className="flex items-center">
                <div
                  className="w-2 h-2 rounded mr-1 flex-shrink-0"
                  style={{ backgroundColor: colors[index % colors.length] }}
                />
                <span className="truncate">{item.label}: {item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full h-full flex items-center justify-center text-gray-500 cursor-pointer hover:bg-gray-200"
      onClick={() => setIsEditing(true)}
    >
      <div className="text-center">
        <div>Chart</div>
        <div className="text-xs mt-1">Click to edit</div>
      </div>
    </div>
  );
};





// Advanced Data Table Controls Component
interface AdvancedDataTableControlsProps {
  block: any;
  onUpdateBlock: (blockId: string, updates: any) => void;
  onClose: () => void;
}

export const AdvancedDataTableControls: React.FC<AdvancedDataTableControlsProps> = ({
  block,
  onUpdateBlock,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<'structure' | 'rows' | 'columns' | 'cells' | 'table'>('structure');
  const [selectedCell, setSelectedCell] = useState<string | null>(null);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [selectedColumn, setSelectedColumn] = useState<number | null>(null);

  const updateDataTableContent = (updates: any) => {
    onUpdateBlock(block.id, {
      content: { ...block.content, ...updates }
    });
  };

  // Update functions for data table structure
  const updateRowStyle = (rowIndex: number, style: any) => {
    const currentRowStyles = block.content.rowStyles || {};
    updateDataTableContent({
      rowStyles: { ...currentRowStyles, [rowIndex]: { ...currentRowStyles[rowIndex], ...style } }
    });
  };

  const updateColumnStyle = (columnIndex: number, style: any) => {
    const currentColumnStyles = block.content.columnStyles || {};
    updateDataTableContent({
      columnStyles: { ...currentColumnStyles, [columnIndex]: { ...currentColumnStyles[columnIndex], ...style } }
    });
  };

  const updateCellStyle = (rowIndex: number, columnIndex: number, style: any) => {
    const cellKey = `${rowIndex}-${columnIndex}`;
    const currentCellStyles = block.content.cellStyles || {};
    updateDataTableContent({
      cellStyles: { ...currentCellStyles, [cellKey]: { ...currentCellStyles[cellKey], ...style } }
    });
  };

  const updateHeaderStyle = (style: any) => {
    updateDataTableContent({
      headerStyle: { ...block.content.headerStyle, ...style }
    });
  };

  const updateTableStyle = (style: any) => {
    updateDataTableContent({
      tableStyle: { ...block.content.tableStyle, ...style }
    });
  };

  // Color presets
  const colorPresets = [
    '#ffffff', '#f8f9fa', '#e9ecef', '#dee2e6', '#ced4da', '#adb5bd',
    '#6c757d', '#495057', '#343a40', '#212529', '#007bff', '#6610f2',
    '#6f42c1', '#e83e8c', '#dc3545', '#fd7e14', '#ffc107', '#28a745',
    '#20c997', '#17a2b8', '#f8f9fa', '#e9ecef'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-[800px] h-[600px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold flex items-center">
            <Database className="w-5 h-5 mr-2" />
            Advanced Data Table Editor
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b overflow-x-auto">
          {[
            { id: 'structure', label: 'Structure' },
            { id: 'rows', label: 'Row Styles' },
            { id: 'columns', label: 'Column Styles' },
            { id: 'cells', label: 'Cell Styles' },
            { id: 'table', label: 'Table Style' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-3 py-2 font-medium text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 p-4 overflow-y-auto">
          {activeTab === 'structure' && (
            <div className="space-y-4">
              <h4 className="font-medium">Data Table Structure</h4>

              {/* Table Overview */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <span className="text-sm font-medium">Headers: </span>
                    <span className="text-sm">{(block.content.headers || []).length}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium">Rows: </span>
                    <span className="text-sm">{(block.content.rows || []).length}</span>
                  </div>
                </div>

                {/* Headers Management */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Headers</label>
                  <input
                    type="text"
                    value={(block.content.headers || []).join(', ')}
                    onChange={(e) => {
                      const headers = e.target.value.split(',').map(h => h.trim()).filter(h => h);
                      updateDataTableContent({ headers });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="Name, Role, Department, Salary"
                  />
                </div>

                {/* Data Management */}
                <div className="space-y-2 mt-4">
                  <label className="block text-sm font-medium text-gray-700">Table Data (JSON format)</label>
                  <textarea
                    value={JSON.stringify(block.content.rows || [], null, 2)}
                    onChange={(e) => {
                      try {
                        const rows = JSON.parse(e.target.value);
                        updateDataTableContent({ rows });
                      } catch (error) {
                        // Invalid JSON, ignore
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono resize-none"
                    rows={6}
                    placeholder='[["John Doe", "Manager", "Sales", "$75,000"]]'
                  />
                </div>

                {/* Quick Actions */}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => {
                      const currentRows = block.content.rows || [];
                      const headers = block.content.headers || [];
                      const newRow = Array(headers.length).fill('New Cell');
                      updateDataTableContent({ rows: [...currentRows, newRow] });
                    }}
                    className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                  >
                    ➕ Add Row
                  </button>
                  <button
                    onClick={() => {
                      const currentHeaders = block.content.headers || [];
                      const currentRows = block.content.rows || [];
                      const newHeaders = [...currentHeaders, 'New Column'];
                      const newRows = currentRows.map(row => [...row, 'New Cell']);
                      updateDataTableContent({ headers: newHeaders, rows: newRows });
                    }}
                    className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600"
                  >
                    Add Column
                  </button>
                </div>
              </div>

              {/* Table Options */}
              <div className="space-y-3">
                <h5 className="font-medium text-sm">Table Options</h5>
                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={block.content.striped !== false}
                      onChange={(e) => updateDataTableContent({ striped: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm">Striped Rows</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={block.content.sortable === true}
                      onChange={(e) => updateDataTableContent({ sortable: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm">Sortable</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={block.content.searchable === true}
                      onChange={(e) => updateDataTableContent({ searchable: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm">Searchable</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={block.content.pagination === true}
                      onChange={(e) => updateDataTableContent({ pagination: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm">Pagination</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'rows' && (
            <div className="space-y-4">
              <h4 className="font-medium">Row Styles</h4>

              {/* Row Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Select Row to Style</label>
                <select
                  value={selectedRow || ''}
                  onChange={(e) => setSelectedRow(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Select a row...</option>
                  {(block.content.rows || []).map((_, index) => (
                    <option key={index} value={index}>
                      Row {index + 1} {index === 0 ? '(Header)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {selectedRow !== null && (
                <div className="space-y-4 border-t pt-4">
                  <h5 className="font-medium text-sm">Style Row {selectedRow + 1}</h5>

                  {/* Background Color */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Background Color</label>
                    <div className="flex gap-2 flex-wrap">
                      {colorPresets.map((color) => (
                        <button
                          key={color}
                          onClick={() => updateRowStyle(selectedRow, { backgroundColor: color })}
                          className="w-8 h-8 rounded border-2 border-gray-300 hover:border-gray-500"
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                    <input
                      type="color"
                      value={block.content.rowStyles?.[selectedRow]?.backgroundColor || '#ffffff'}
                      onChange={(e) => updateRowStyle(selectedRow, { backgroundColor: e.target.value })}
                      className="w-full h-10 border border-gray-300 rounded"
                    />
                  </div>

                  {/* Text Color */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Text Color</label>
                    <input
                      type="color"
                      value={block.content.rowStyles?.[selectedRow]?.textColor || '#000000'}
                      onChange={(e) => updateRowStyle(selectedRow, { textColor: e.target.value })}
                      className="w-full h-10 border border-gray-300 rounded"
                    />
                  </div>

                  {/* Font Weight */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Font Weight</label>
                    <select
                      value={block.content.rowStyles?.[selectedRow]?.fontWeight || 'normal'}
                      onChange={(e) => updateRowStyle(selectedRow, { fontWeight: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="normal">Normal</option>
                      <option value="bold">Bold</option>
                      <option value="semibold">Semi Bold</option>
                      <option value="extrabold">Extra Bold</option>
                    </select>
                  </div>

                  {/* Text Alignment */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Text Alignment</label>
                    <select
                      value={block.content.rowStyles?.[selectedRow]?.textAlign || 'left'}
                      onChange={(e) => updateRowStyle(selectedRow, { textAlign: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </div>

                  {/* Font Size */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Font Size (px)</label>
                    <input
                      type="number"
                      value={block.content.rowStyles?.[selectedRow]?.fontSize || 14}
                      onChange={(e) => updateRowStyle(selectedRow, { fontSize: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      min="8"
                      max="72"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'columns' && (
            <div className="space-y-4">
              <h4 className="font-medium">Column Styles</h4>

              {/* Column Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Select Column to Style</label>
                <select
                  value={selectedColumn || ''}
                  onChange={(e) => setSelectedColumn(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Select a column...</option>
                  {(block.content.headers || []).map((header, index) => (
                    <option key={index} value={index}>
                      Column {index + 1}: {header}
                    </option>
                  ))}
                </select>
              </div>

              {selectedColumn !== null && (
                <div className="space-y-4 border-t pt-4">
                  <h5 className="font-medium text-sm">Style Column {selectedColumn + 1}</h5>

                  {/* Background Color */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Background Color</label>
                    <div className="flex gap-2 flex-wrap">
                      {colorPresets.map((color) => (
                        <button
                          key={color}
                          onClick={() => updateColumnStyle(selectedColumn, { backgroundColor: color })}
                          className="w-8 h-8 rounded border-2 border-gray-300 hover:border-gray-500"
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                    <input
                      type="color"
                      value={block.content.columnStyles?.[selectedColumn]?.backgroundColor || '#ffffff'}
                      onChange={(e) => updateColumnStyle(selectedColumn, { backgroundColor: e.target.value })}
                      className="w-full h-10 border border-gray-300 rounded"
                    />
                  </div>

                  {/* Text Color */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Text Color</label>
                    <input
                      type="color"
                      value={block.content.columnStyles?.[selectedColumn]?.textColor || '#000000'}
                      onChange={(e) => updateColumnStyle(selectedColumn, { textColor: e.target.value })}
                      className="w-full h-10 border border-gray-300 rounded"
                    />
                  </div>

                  {/* Font Weight */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Font Weight</label>
                    <select
                      value={block.content.columnStyles?.[selectedColumn]?.fontWeight || 'normal'}
                      onChange={(e) => updateColumnStyle(selectedColumn, { fontWeight: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="normal">Normal</option>
                      <option value="bold">Bold</option>
                      <option value="semibold">Semi Bold</option>
                      <option value="extrabold">Extra Bold</option>
                    </select>
                  </div>

                  {/* Text Alignment */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Text Alignment</label>
                    <select
                      value={block.content.columnStyles?.[selectedColumn]?.textAlign || 'left'}
                      onChange={(e) => updateColumnStyle(selectedColumn, { textAlign: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </div>

                  {/* Font Size */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Font Size (px)</label>
                    <input
                      type="number"
                      value={block.content.columnStyles?.[selectedColumn]?.fontSize || 14}
                      onChange={(e) => updateColumnStyle(selectedColumn, { fontSize: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      min="8"
                      max="72"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'cells' && (
            <div className="space-y-4">
              <h4 className="font-medium">Individual Cell Styles</h4>

              {/* Cell Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Select Cell to Style</label>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={selectedRow || ''}
                    onChange={(e) => setSelectedRow(e.target.value ? parseInt(e.target.value) : null)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="">Select row...</option>
                    {(block.content.rows || []).map((_, index) => (
                      <option key={index} value={index}>
                        Row {index + 1}
                      </option>
                    ))}
                  </select>
                  <select
                    value={selectedColumn || ''}
                    onChange={(e) => setSelectedColumn(e.target.value ? parseInt(e.target.value) : null)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="">Select column...</option>
                    {(block.content.headers || []).map((header, index) => (
                      <option key={index} value={index}>
                        {header}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {selectedRow !== null && selectedColumn !== null && (
                <div className="space-y-4 border-t pt-4">
                  <h5 className="font-medium text-sm">
                    Style Cell [{selectedRow + 1}, {selectedColumn + 1}]
                  </h5>

                  {/* Current Cell Value */}
                  <div className="bg-gray-50 p-3 rounded">
                    <span className="text-sm font-medium">Current Value: </span>
                    <span className="text-sm">
                      {block.content.rows?.[selectedRow]?.[selectedColumn] || 'Empty'}
                    </span>
                  </div>

                  {/* Background Color */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Background Color</label>
                    <div className="flex gap-2 flex-wrap">
                      {colorPresets.map((color) => (
                        <button
                          key={color}
                          onClick={() => updateCellStyle(selectedRow, selectedColumn, { backgroundColor: color })}
                          className="w-8 h-8 rounded border-2 border-gray-300 hover:border-gray-500"
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                    <input
                      type="color"
                      value={block.content.cellStyles?.[`${selectedRow}-${selectedColumn}`]?.backgroundColor || '#ffffff'}
                      onChange={(e) => updateCellStyle(selectedRow, selectedColumn, { backgroundColor: e.target.value })}
                      className="w-full h-10 border border-gray-300 rounded"
                    />
                  </div>

                  {/* Text Color */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Text Color</label>
                    <input
                      type="color"
                      value={block.content.cellStyles?.[`${selectedRow}-${selectedColumn}`]?.textColor || '#000000'}
                      onChange={(e) => updateCellStyle(selectedRow, selectedColumn, { textColor: e.target.value })}
                      className="w-full h-10 border border-gray-300 rounded"
                    />
                  </div>

                  {/* Font Weight */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Font Weight</label>
                    <select
                      value={block.content.cellStyles?.[`${selectedRow}-${selectedColumn}`]?.fontWeight || 'normal'}
                      onChange={(e) => updateCellStyle(selectedRow, selectedColumn, { fontWeight: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="normal">Normal</option>
                      <option value="bold">Bold</option>
                      <option value="semibold">Semi Bold</option>
                      <option value="extrabold">Extra Bold</option>
                    </select>
                  </div>

                  {/* Text Alignment */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Text Alignment</label>
                    <select
                      value={block.content.cellStyles?.[`${selectedRow}-${selectedColumn}`]?.textAlign || 'left'}
                      onChange={(e) => updateCellStyle(selectedRow, selectedColumn, { textAlign: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </div>

                  {/* Font Size */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Font Size (px)</label>
                    <input
                      type="number"
                      value={block.content.cellStyles?.[`${selectedRow}-${selectedColumn}`]?.fontSize || 14}
                      onChange={(e) => updateCellStyle(selectedRow, selectedColumn, { fontSize: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      min="8"
                      max="72"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'table' && (
            <div className="space-y-4">
              <h4 className="font-medium">Table Style</h4>

              {/* Header Styles */}
              <div className="space-y-4 border-b pb-4">
                <h5 className="font-medium text-sm">Header Styles</h5>

                {/* Header Background Color */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Header Background Color</label>
                  <div className="flex gap-2 flex-wrap">
                    {colorPresets.map((color) => (
                      <button
                        key={color}
                        onClick={() => updateHeaderStyle({ backgroundColor: color })}
                        className="w-8 h-8 rounded border-2 border-gray-300 hover:border-gray-500"
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                  <input
                    type="color"
                    value={block.content.headerStyle?.backgroundColor || '#f3f4f6'}
                    onChange={(e) => updateHeaderStyle({ backgroundColor: e.target.value })}
                    className="w-full h-10 border border-gray-300 rounded"
                  />
                </div>

                {/* Header Text Color */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Header Text Color</label>
                  <input
                    type="color"
                    value={block.content.headerStyle?.textColor || '#374151'}
                    onChange={(e) => updateHeaderStyle({ textColor: e.target.value })}
                    className="w-full h-10 border border-gray-300 rounded"
                  />
                </div>

                {/* Header Font Weight */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Header Font Weight</label>
                  <select
                    value={block.content.headerStyle?.fontWeight || 'bold'}
                    onChange={(e) => updateHeaderStyle({ fontWeight: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="normal">Normal</option>
                    <option value="bold">Bold</option>
                    <option value="semibold">Semi Bold</option>
                    <option value="extrabold">Extra Bold</option>
                  </select>
                </div>
              </div>

              {/* Table Styles */}
              <div className="space-y-4">
                <h5 className="font-medium text-sm">Table Styles</h5>

                {/* Table Background Color */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Table Background Color</label>
                  <input
                    type="color"
                    value={block.content.tableStyle?.backgroundColor || '#ffffff'}
                    onChange={(e) => updateTableStyle({ backgroundColor: e.target.value })}
                    className="w-full h-10 border border-gray-300 rounded"
                  />
                </div>

                {/* Border Color */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Border Color</label>
                  <input
                    type="color"
                    value={block.content.tableStyle?.borderColor || '#e5e7eb'}
                    onChange={(e) => updateTableStyle({ borderColor: e.target.value })}
                    className="w-full h-10 border border-gray-300 rounded"
                  />
                </div>

                {/* Border Width */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Border Width (px)</label>
                  <input
                    type="number"
                    value={block.content.tableStyle?.borderWidth || 1}
                    onChange={(e) => updateTableStyle({ borderWidth: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    min="0"
                    max="10"
                  />
                </div>

                {/* Border Style */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Border Style</label>
                  <select
                    value={block.content.tableStyle?.borderStyle || 'solid'}
                    onChange={(e) => updateTableStyle({ borderStyle: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="solid">Solid</option>
                    <option value="dashed">Dashed</option>
                    <option value="dotted">Dotted</option>
                  </select>
                </div>

                {/* Border Radius */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Border Radius (px)</label>
                  <input
                    type="number"
                    value={block.content.tableStyle?.borderRadius || 0}
                    onChange={(e) => updateTableStyle({ borderRadius: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    min="0"
                    max="20"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Advanced Table Controls Component
interface AdvancedTableControlsProps {
  block: any;
  onUpdateBlock: (blockId: string, updates: any) => void;
  onClose: () => void;
}

export const AdvancedTableControls: React.FC<AdvancedTableControlsProps> = ({
  block,
  onUpdateBlock,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<'structure' | 'rows' | 'columns' | 'cells' | 'table'>('structure');
  const [selectedCell, setSelectedCell] = useState<string | null>(null);
  const [selectedRow, setSelectedRow] = useState<string | null>(null);
  const [selectedColumn, setSelectedColumn] = useState<number | null>(null);

  const updateTableContent = (updates: any) => {
    onUpdateBlock(block.id, {
      content: { ...block.content, ...updates }
    });
  };

  // Enhanced update functions for new structure
  const updateRowStyle = (rowId: string, style: any) => {
    const tableRows = block.content.rows || [];
    const updatedRows = tableRows.map((row: any) =>
      row.id === rowId ? { ...row, style: { ...row.style, ...style } } : row
    );
    updateTableContent({ rows: updatedRows });
  };

  const updateColumnStyle = (columnIndex: number, style: any) => {
    const tableColumns = block.content.columns || [];
    const updatedColumns = [...tableColumns];
    if (!updatedColumns[columnIndex]) {
      updatedColumns[columnIndex] = { id: `col-${columnIndex}`, width: 120 };
    }
    updatedColumns[columnIndex] = {
      ...updatedColumns[columnIndex],
      style: { ...updatedColumns[columnIndex].style, ...style }
    };
    updateTableContent({ columns: updatedColumns });
  };

  const updateCellStyle = (rowId: string, cellId: string, style: any) => {
    const tableRows = block.content.rows || [];
    const updatedRows = tableRows.map((row: any) =>
      row.id === rowId ? {
        ...row,
        cells: row.cells.map((cell: any) =>
          cell.id === cellId ? { ...cell, style: { ...cell.style, ...style } } : cell
        )
      } : row
    );
    updateTableContent({ rows: updatedRows });
  };

  const updateHeaderStyle = (style: any) => {
    updateTableContent({
      headerStyle: { ...block.content.headerStyle, ...style }
    });
  };

  const updateTableStyle = (style: any) => {
    updateTableContent({
      tableStyle: { ...block.content.tableStyle, ...style }
    });
  };

  // Color presets
  const colorPresets = [
    '#ffffff', '#f8f9fa', '#e9ecef', '#dee2e6', '#ced4da', '#adb5bd',
    '#6c757d', '#495057', '#343a40', '#212529', '#007bff', '#6610f2',
    '#6f42c1', '#e83e8c', '#dc3545', '#fd7e14', '#ffc107', '#28a745',
    '#20c997', '#17a2b8', '#f8f9fa', '#e9ecef'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-[800px] h-[600px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold flex items-center">
            <TableIcon className="w-5 h-5 mr-2" />
            Advanced Table Editor
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>

        {/* Enhanced Tabs */}
        <div className="flex border-b overflow-x-auto">
          {[
            { id: 'structure', label: 'Structure' },
            { id: 'rows', label: 'Row Styles' },
            { id: 'columns', label: 'Column Styles' },
            { id: 'cells', label: 'Cell Styles' },
            { id: 'table', label: 'Table Style' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-3 py-2 font-medium text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 p-4 overflow-y-auto">
          {activeTab === 'structure' && (
            <div className="space-y-4">
              <h4 className="font-medium">Enhanced Table Structure</h4>

              {/* Table Overview */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <span className="text-sm font-medium">Rows: </span>
                    <span className="text-sm">{(block.content.rows || []).length}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium">Max Columns: </span>
                    <span className="text-sm">
                      {Math.max(...(block.content.rows || []).map((row: any) => row.cells.length), 0)}
                    </span>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const tableRows = block.content.rows || [];
                      const newRowId = `row-${Date.now()}`;
                      // Create a simple 1-column row by default (user can modify it)
                      const newCells = [{
                        id: `cell-${newRowId}-0`,
                        content: `Row ${tableRows.length + 1}`,
                        colspan: 1,
                        rowspan: 1
                      }];

                      const newRow = { id: newRowId, cells: newCells };
                      updateTableContent({ rows: [...tableRows, newRow] });
                    }}
                    className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                  >
                    ➕ Add Empty Row
                  </button>
                  <button
                    onClick={() => {
                      const tableRows = block.content.rows || [];
                      const tableColumns = block.content.columns || [];

                      const updatedRows = tableRows.map((row: any, rowIndex: number) => ({
                        ...row,
                        cells: [...row.cells, {
                          id: `cell-${row.id}-${row.cells.length}`,
                          content: `Cell ${rowIndex + 1},${row.cells.length + 1}`,
                          colspan: 1,
                          rowspan: 1
                        }]
                      }));

                      const newColumnId = `col-${Date.now()}`;
                      const updatedColumns = [...tableColumns, { id: newColumnId, width: 120 }];

                      updateTableContent({ rows: updatedRows, columns: updatedColumns });
                    }}
                    className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600"
                  >
                    Add Column
                  </button>
                </div>
              </div>

              {/* Header Row Toggle */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="hasHeader"
                  checked={block.content.hasHeader || false}
                  onChange={(e) => updateTableContent({ hasHeader: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="hasHeader" className="text-sm font-medium">
                  First row is header
                </label>
              </div>

              {/* Row Layout Templates */}
              <div className="space-y-3">
                <h5 className="font-medium text-sm">Add Row with Layout</h5>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => {
                      const tableRows = block.content.rows || [];
                      const newRowId = `row-${Date.now()}`;
                      const newCells = [{
                        id: `cell-${newRowId}-0`,
                        content: 'Full Width Heading',
                        colspan: 4, // Spans all 4 columns
                        rowspan: 1
                      }];
                      const newRow = {
                        id: newRowId,
                        cells: newCells,
                        style: {
                          backgroundColor: '#f3f4f6',
                          fontWeight: 'bold',
                          textAlign: 'center'
                        }
                      };
                      updateTableContent({ rows: [...tableRows, newRow] });
                    }}
                    className="px-3 py-2 bg-purple-500 text-white rounded text-sm hover:bg-purple-600"
                  >
                    📄 Full Width Row
                  </button>
                  <button
                    onClick={() => {
                      const tableRows = block.content.rows || [];
                      const newRowId = `row-${Date.now()}`;
                      const newCells = [
                        {
                          id: `cell-${newRowId}-0`,
                          content: 'Column 1',
                          colspan: 2, // Spans 2 columns
                          rowspan: 1
                        },
                        {
                          id: `cell-${newRowId}-1`,
                          content: 'Column 2',
                          colspan: 2, // Spans 2 columns
                          rowspan: 1
                        }
                      ];
                      const newRow = { id: newRowId, cells: newCells };
                      updateTableContent({ rows: [...tableRows, newRow] });
                    }}
                    className="px-3 py-2 bg-indigo-500 text-white rounded text-sm hover:bg-indigo-600"
                  >
                    📊 2 Column Row
                  </button>
                  <button
                    onClick={() => {
                      const tableRows = block.content.rows || [];
                      const newRowId = `row-${Date.now()}`;
                      const newCells = [
                        {
                          id: `cell-${newRowId}-0`,
                          content: 'Col 1',
                          colspan: 1,
                          rowspan: 1
                        },
                        {
                          id: `cell-${newRowId}-1`,
                          content: 'Wide Column',
                          colspan: 2, // Spans 2 columns
                          rowspan: 1
                        },
                        {
                          id: `cell-${newRowId}-2`,
                          content: 'Col 3',
                          colspan: 1,
                          rowspan: 1
                        }
                      ];
                      const newRow = { id: newRowId, cells: newCells };
                      updateTableContent({ rows: [...tableRows, newRow] });
                    }}
                    className="px-3 py-2 bg-teal-500 text-white rounded text-sm hover:bg-teal-600"
                  >
                    📋 3 Column Row
                  </button>
                  <button
                    onClick={() => {
                      const tableRows = block.content.rows || [];
                      const newRowId = `row-${Date.now()}`;
                      const newCells = Array.from({ length: 4 }, (_, i) => ({
                        id: `cell-${newRowId}-${i}`,
                        content: `Cell ${i + 1}`,
                        colspan: 1,
                        rowspan: 1
                      }));
                      const newRow = { id: newRowId, cells: newCells };
                      updateTableContent({ rows: [...tableRows, newRow] });
                    }}
                    className="px-3 py-2 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                  >
                    🔲 4 Column Row
                  </button>
                </div>
              </div>

              {/* Cell Merging Controls */}
              {selectedCell && (
                <div className="space-y-3 border-t pt-4">
                  <h5 className="font-medium text-sm">Selected Cell Controls</h5>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => {
                        const tableRows = block.content.rows || [];
                        const updatedRows = tableRows.map((row: any) => ({
                          ...row,
                          cells: row.cells.map((cell: any) =>
                            cell.id === selectedCell ? { ...cell, colspan: Math.min(cell.colspan + 1, 4) } : cell
                          )
                        }));
                        updateTableContent({ rows: updatedRows });
                      }}
                      className="px-3 py-1 bg-orange-500 text-white rounded text-sm hover:bg-orange-600"
                    >
                      ➡️ Merge Right
                    </button>
                    <button
                      onClick={() => {
                        const tableRows = block.content.rows || [];
                        const updatedRows = tableRows.map((row: any) => ({
                          ...row,
                          cells: row.cells.map((cell: any) =>
                            cell.id === selectedCell ? { ...cell, colspan: Math.max(cell.colspan - 1, 1) } : cell
                          )
                        }));
                        updateTableContent({ rows: updatedRows });
                      }}
                      className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                    >
                      ✂️ Split Cell
                    </button>
                  </div>
                  <div className="text-xs text-gray-600">
                    Current cell spans {(block.content.rows || []).find((row: any) =>
                      row.cells.find((cell: any) => cell.id === selectedCell)
                    )?.cells.find((cell: any) => cell.id === selectedCell)?.colspan || 1} column(s)
                  </div>
                </div>
              )}

              {/* Enhanced Table Preview */}
              <div className="space-y-3">
                <h5 className="font-medium text-sm">Table Preview (with colspan)</h5>
                <div className="border rounded p-3 bg-gray-50 max-h-40 overflow-auto">
                  <div className="space-y-1">
                    {(block.content.rows || []).map((row: any, index: number) => (
                      <div key={row.id} className="flex items-center space-x-1">
                        <div className="text-xs text-gray-500 w-12 flex-shrink-0">Row {index + 1}:</div>
                        <div className="flex space-x-1 flex-wrap">
                          {row.cells.map((cell: any) => (
                            <div
                              key={cell.id}
                              className={`text-xs px-2 py-1 rounded border cursor-pointer transition-colors ${
                                cell.colspan > 1
                                  ? 'bg-yellow-100 border-yellow-300 text-yellow-800'
                                  : 'bg-white border-gray-300'
                              } ${
                                selectedCell === cell.id ? 'ring-2 ring-blue-500' : ''
                              }`}
                              style={{ minWidth: `${cell.colspan * 40}px` }}
                              onClick={() => setSelectedCell(cell.id)}
                            >
                              {cell.content.substring(0, 12)}{cell.content.length > 12 ? '...' : ''}
                              {cell.colspan > 1 && <span className="ml-1 font-bold">({cell.colspan})</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'rows' && (
            <div className="space-y-4">
              <h4 className="font-medium">Row-Level Styling</h4>

              {/* Row Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Select Row to Style</label>
                <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto">
                  {(block.content.rows || []).map((row: any, index: number) => (
                    <button
                      key={row.id}
                      onClick={() => setSelectedRow(row.id)}
                      className={`p-2 text-left text-sm border rounded ${
                        selectedRow === row.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      Row {index + 1} ({row.cells.length} cells)
                      {block.content.hasHeader && index === 0 && (
                        <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-1 rounded">Header</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Row Styling Controls */}
              {selectedRow && (
                <div className="space-y-4 border-t pt-4">
                  <h5 className="font-medium text-sm">Style Row {(block.content.rows || []).findIndex((r: any) => r.id === selectedRow) + 1}</h5>

                  {/* Background Color */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Background Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={(block.content.rows || []).find((r: any) => r.id === selectedRow)?.style?.backgroundColor || '#ffffff'}
                        onChange={(e) => updateRowStyle(selectedRow, { backgroundColor: e.target.value })}
                        className="w-12 h-8 rounded border"
                      />
                      <div className="flex flex-wrap gap-1">
                        {colorPresets.slice(0, 12).map((color) => (
                          <button
                            key={color}
                            onClick={() => updateRowStyle(selectedRow, { backgroundColor: color })}
                            className="w-6 h-6 rounded border border-gray-300"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Text Color */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Text Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={(block.content.rows || []).find((r: any) => r.id === selectedRow)?.style?.textColor || '#000000'}
                        onChange={(e) => updateRowStyle(selectedRow, { textColor: e.target.value })}
                        className="w-12 h-8 rounded border"
                      />
                      <div className="flex gap-1">
                        {['#000000', '#ffffff', '#374151', '#6b7280', '#ef4444', '#3b82f6', '#10b981'].map((color) => (
                          <button
                            key={color}
                            onClick={() => updateRowStyle(selectedRow, { textColor: color })}
                            className="w-6 h-6 rounded border border-gray-300"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Font Weight */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Font Weight</label>
                    <select
                      value={(block.content.rows || []).find((r: any) => r.id === selectedRow)?.style?.fontWeight || 'normal'}
                      onChange={(e) => updateRowStyle(selectedRow, { fontWeight: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="normal">Normal</option>
                      <option value="bold">Bold</option>
                      <option value="semibold">Semi Bold</option>
                      <option value="extrabold">Extra Bold</option>
                    </select>
                  </div>

                  {/* Text Alignment */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Text Alignment</label>
                    <div className="flex gap-2">
                      {['left', 'center', 'right'].map((align) => (
                        <button
                          key={align}
                          onClick={() => updateRowStyle(selectedRow, { textAlign: align })}
                          className={`px-3 py-1 text-sm border rounded ${
                            (block.content.rows || []).find((r: any) => r.id === selectedRow)?.style?.textAlign === align
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          {align.charAt(0).toUpperCase() + align.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'columns' && (
            <div className="space-y-4">
              <h4 className="font-medium">Column-Level Styling</h4>

              {/* Column Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Select Column to Style</label>
                <div className="grid grid-cols-3 gap-2">
                  {Array.from({ length: Math.max(...(block.content.rows || []).map((row: any) => row.cells.length), 0) }, (_, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedColumn(index)}
                      className={`p-2 text-center text-sm border rounded ${
                        selectedColumn === index
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      Column {index + 1}
                    </button>
                  ))}
                </div>
              </div>

              {/* Column Styling Controls */}
              {selectedColumn !== null && (
                <div className="space-y-4 border-t pt-4">
                  <h5 className="font-medium text-sm">Style Column {selectedColumn + 1}</h5>

                  {/* Background Color */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Background Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={(block.content.columns || [])[selectedColumn]?.style?.backgroundColor || '#ffffff'}
                        onChange={(e) => updateColumnStyle(selectedColumn, { backgroundColor: e.target.value })}
                        className="w-12 h-8 rounded border"
                      />
                      <div className="flex flex-wrap gap-1">
                        {colorPresets.slice(0, 12).map((color) => (
                          <button
                            key={color}
                            onClick={() => updateColumnStyle(selectedColumn, { backgroundColor: color })}
                            className="w-6 h-6 rounded border border-gray-300"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Column Width */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Column Width (px)</label>
                    <input
                      type="number"
                      min="50"
                      max="500"
                      value={(block.content.columns || [])[selectedColumn]?.width || 120}
                      onChange={(e) => {
                        const tableColumns = block.content.columns || [];
                        const updatedColumns = [...tableColumns];
                        if (!updatedColumns[selectedColumn]) {
                          updatedColumns[selectedColumn] = { id: `col-${selectedColumn}`, width: 120 };
                        }
                        updatedColumns[selectedColumn] = {
                          ...updatedColumns[selectedColumn],
                          width: parseInt(e.target.value) || 120
                        };
                        updateTableContent({ columns: updatedColumns });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'header' && (
            <div className="space-y-4">
              <h4 className="font-medium">Header Row Styling</h4>

              {/* Background Color */}
              <div>
                <label className="block text-sm font-medium mb-2">Background Color</label>
                <div className="grid grid-cols-11 gap-1 mb-2">
                  {colorPresets.map((color) => (
                    <button
                      key={color}
                      onClick={() => updateHeaderStyle({ backgroundColor: color })}
                      className="w-8 h-8 rounded border-2 border-gray-300 hover:border-gray-400"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <input
                  type="color"
                  value={block.content.headerStyle?.backgroundColor || '#f3f4f6'}
                  onChange={(e) => updateHeaderStyle({ backgroundColor: e.target.value })}
                  className="w-full h-10 rounded border"
                />
              </div>

              {/* Text Color */}
              <div>
                <label className="block text-sm font-medium mb-2">Text Color</label>
                <input
                  type="color"
                  value={block.content.headerStyle?.textColor || '#1f2937'}
                  onChange={(e) => updateHeaderStyle({ textColor: e.target.value })}
                  className="w-full h-10 rounded border"
                />
              </div>

              {/* Font Weight */}
              <div>
                <label className="block text-sm font-medium mb-1">Font Weight</label>
                <select
                  value={block.content.headerStyle?.fontWeight || 'bold'}
                  onChange={(e) => updateHeaderStyle({ fontWeight: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="normal">Normal</option>
                  <option value="bold">Bold</option>
                  <option value="600">Semi Bold</option>
                  <option value="700">Bold</option>
                  <option value="800">Extra Bold</option>
                </select>
              </div>

              {/* Text Alignment */}
              <div>
                <label className="block text-sm font-medium mb-1">Text Alignment</label>
                <select
                  value={block.content.headerStyle?.textAlign || 'left'}
                  onChange={(e) => updateHeaderStyle({ textAlign: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </div>
            </div>
          )}

          {activeTab === 'cells' && (
            <div className="space-y-4">
              <h4 className="font-medium">Individual Cell Styling</h4>
              <p className="text-sm text-gray-600">
                Select a cell below to customize its appearance.
              </p>

              {/* Cell Selection Grid */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="text-sm font-medium mb-2">Select Cell to Style:</div>
                <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${block.content.cols || 3}, 1fr)` }}>
                  {Array.from({ length: (block.content.rows || 3) * (block.content.cols || 3) }).map((_, index) => {
                    const row = Math.floor(index / (block.content.cols || 3));
                    const col = index % (block.content.cols || 3);
                    const cellKey = `${row}-${col}`;
                    const isSelected = selectedCell === cellKey;
                    const isHeader = block.content.hasHeader && row === 0;

                    return (
                      <button
                        key={cellKey}
                        onClick={() => setSelectedCell(cellKey)}
                        className={`p-2 text-xs border rounded ${
                          isSelected
                            ? 'bg-blue-500 text-white border-blue-600'
                            : isHeader
                            ? 'bg-gray-200 text-gray-700 border-gray-300 hover:bg-gray-300'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                        }`}
                        title={`Cell ${row + 1},${col + 1}${isHeader ? ' (Header)' : ''}`}
                      >
                        {row + 1},{col + 1}
                      </button>
                    );
                  })}
                </div>
              </div>

              {selectedCell ? (
                <div className="space-y-4">
                  <div className="text-sm font-medium">
                    Editing Cell: {selectedCell} {block.content.hasHeader && selectedCell.startsWith('0-') ? '(Header)' : ''}
                  </div>

                  {/* Cell Background Color */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Background Color</label>
                    <div className="grid grid-cols-11 gap-1 mb-2">
                      {colorPresets.map((color) => (
                        <button
                          key={color}
                          onClick={() => updateCellStyle(selectedCell, { backgroundColor: color })}
                          className="w-8 h-8 rounded border-2 border-gray-300 hover:border-gray-400"
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                    <input
                      type="color"
                      value={block.content.cellStyles?.[selectedCell]?.backgroundColor || '#ffffff'}
                      onChange={(e) => updateCellStyle(selectedCell, { backgroundColor: e.target.value })}
                      className="w-full h-10 rounded border"
                    />
                  </div>

                  {/* Cell Text Color */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Text Color</label>
                    <input
                      type="color"
                      value={block.content.cellStyles?.[selectedCell]?.textColor || '#000000'}
                      onChange={(e) => updateCellStyle(selectedCell, { textColor: e.target.value })}
                      className="w-full h-10 rounded border"
                    />
                  </div>

                  {/* Cell Text Alignment */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Text Alignment</label>
                    <select
                      value={block.content.cellStyles?.[selectedCell]?.textAlign || 'left'}
                      onChange={(e) => updateCellStyle(selectedCell, { textAlign: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </div>

                  {/* Cell Font Weight */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Font Weight</label>
                    <select
                      value={block.content.cellStyles?.[selectedCell]?.fontWeight || 'normal'}
                      onChange={(e) => updateCellStyle(selectedCell, { fontWeight: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="normal">Normal</option>
                      <option value="bold">Bold</option>
                      <option value="600">Semi Bold</option>
                      <option value="700">Bold</option>
                      <option value="800">Extra Bold</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Select a cell above to customize its styling
                </div>
              )}
            </div>
          )}

          {activeTab === 'table' && (
            <div className="space-y-4">
              <h4 className="font-medium">Overall Table Styling</h4>

              {/* Border Color */}
              <div>
                <label className="block text-sm font-medium mb-2">Border Color</label>
                <input
                  type="color"
                  value={block.content.tableStyle?.borderColor || '#e5e7eb'}
                  onChange={(e) => updateTableStyle({ borderColor: e.target.value })}
                  className="w-full h-10 rounded border"
                />
              </div>

              {/* Border Width */}
              <div>
                <label className="block text-sm font-medium mb-1">Border Width</label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  value={block.content.tableStyle?.borderWidth || 1}
                  onChange={(e) => updateTableStyle({ borderWidth: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              {/* Border Style */}
              <div>
                <label className="block text-sm font-medium mb-1">Border Style</label>
                <select
                  value={block.content.tableStyle?.borderStyle || 'solid'}
                  onChange={(e) => updateTableStyle({ borderStyle: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="solid">Solid</option>
                  <option value="dashed">Dashed</option>
                  <option value="dotted">Dotted</option>
                  <option value="double">Double</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
